import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Gift, Loader2, PartyPopper, Sparkles } from "lucide-react";
import { authHeader, getUserToken } from "@/utils/userAuth";
import { extractApiError } from "@/utils/apiError";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* -------------------------------------------------------------------------- */
/*  UnlockFreePage — landing the user reaches via an admin-generated magic    */
/*  link. They auto-logged in via /corso/auto-login → here they see a         */
/*  celebratory page with a single CTA "Sblocca ora gratis 🎁".               */
/* -------------------------------------------------------------------------- */

const KIND_CONFIG = {
  bundle_234: {
    headline: "Hai sbloccato",
    highlight: "le Lezioni 2, 3 e 4",
    tagline: "Un regalo per te. Pronto a continuare il percorso?",
    bullets: [
      "Lezione 2 — Il modello a 6 assi",
      "Lezione 3 — La cornice dietro al modello",
      "Lezione 4 — Consolidare il cambiamento",
    ],
    ctaLabel: "🎁 Sblocca ora le Lezioni 2, 3 e 4",
    successCta: "Vai alla Lezione 2 →",
    successHref: "/corso/lezione/2",
  },
  full_course: {
    headline: "Hai sbloccato",
    highlight: "tutto il Corso Completo",
    tagline: "Le 9 lezioni di Neuroscienza Pratica · Figlio a Carico, tue.",
    bullets: [
      "Tutte le 9 lezioni — accesso permanente",
      "Aggiornamenti futuri inclusi",
      "Materiali extra e protocollo operativo (L9)",
    ],
    ctaLabel: "🎁 Sblocca ora il Corso Completo",
    successCta: "Vai all'Area Membri →",
    successHref: "/corso/area-membri",
  },
};

function Confetti({ show }) {
  // Lightweight CSS-only confetti — no external dep
  if (!show) return null;
  const pieces = Array.from({ length: 60 }).map((_, i) => {
    const left = Math.random() * 100;
    const delay = Math.random() * 0.5;
    const duration = 2.2 + Math.random() * 1.8;
    const rotate = Math.random() * 360;
    const colors = ["#D55D39", "#1A2A3A", "#22c55e", "#eab308", "#3b82f6", "#ec4899"];
    const bg = colors[i % colors.length];
    return (
      <span
        key={i}
        aria-hidden
        style={{
          position: "absolute",
          left: `${left}%`,
          top: "-20px",
          width: 8 + Math.random() * 6,
          height: 14 + Math.random() * 8,
          background: bg,
          transform: `rotate(${rotate}deg)`,
          animation: `np-confetti-fall ${duration}s ${delay}s linear forwards`,
          opacity: 0.9,
          borderRadius: 2,
        }}
      />
    );
  });
  return (
    <>
      <style>{`
        @keyframes np-confetti-fall {
          0%   { transform: translateY(0) rotate(0deg);   opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0.3; }
        }
        @keyframes np-pop-in {
          0%   { transform: scale(0.6); opacity: 0; }
          60%  { transform: scale(1.06); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
      <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden" aria-hidden>
        {pieces}
      </div>
    </>
  );
}

export default function UnlockFreePage() {
  const { kind } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const tid = params.get("tid") || "";
  const cfg = KIND_CONFIG[kind];

  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [error, setError] = useState("");
  const clickedSentRef = useRef(false);

  useEffect(() => {
    if (!cfg) { setLoading(false); return; }
    if (!getUserToken()) {
      navigate("/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/users/me`, { headers: authHeader() });
        setMe(data || null);
      } catch (err) {
        setError(extractApiError(err, "Impossibile caricare il profilo."));
      }
      // Best-effort: log the click. Ignore errors (e.g. expired link).
      if (tid && !clickedSentRef.current) {
        clickedSentRef.current = true;
        try { await axios.post(`${API}/leads/track-click`, { tracking_id: tid }, { headers: authHeader() }); } catch { /* noop */ }
      }
      setLoading(false);
    })();
  }, [cfg, tid, navigate]);

  const handleUnlock = async () => {
    if (submitting || unlocked) return;
    setSubmitting(true);
    setError("");
    try {
      await axios.post(
        `${API}/leads/claim-unlock`,
        { tracking_id: tid, kind },
        { headers: authHeader() },
      );
      setUnlocked(true);
      // Auto-redirect after a short celebration so the user lands directly on
      // the content they just unlocked (Lezione 2 for bundle / area membri for
      // full course). The manual CTA stays available for the brief delay.
      setTimeout(() => { window.location.href = cfg.successHref; }, 2600);
    } catch (err) {
      setError(extractApiError(err, "Errore durante lo sblocco. Riprova."));
    } finally {
      setSubmitting(false);
    }
  };

  if (!cfg) {
    return (
      <main className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--np-primary)", color: "#fff" }}>
        <div className="max-w-md text-center">
          <h1 className="text-3xl font-semibold mb-3" style={{ fontFamily: "Merriweather, serif" }}>Link non valido</h1>
          <p style={{ color: "rgba(255,255,255,0.7)" }}>Il tipo di sblocco non è riconosciuto.</p>
        </div>
      </main>
    );
  }

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center" style={{ background: "var(--np-primary)" }}>
        <Loader2 className="animate-spin" size={28} style={{ color: "var(--np-secondary)" }} />
      </main>
    );
  }

  const firstName = (me?.name || "").split(/\s+/)[0] || "";

  return (
    <main
      className="min-h-screen relative overflow-hidden flex items-center justify-center px-6 py-16"
      style={{ background: "var(--np-primary)", color: "#fff" }}
      data-testid="unlock-free-page"
    >
      <Confetti show={unlocked} />
      {/* Decorative glow */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 70% 50% at 50% -5%, rgba(213,93,57,0.30), transparent 70%)",
        }}
      />

      <div
        className="relative w-full"
        style={{ maxWidth: 640, animation: "np-pop-in 0.6s ease-out both" }}
      >
        <div
          className="rounded-3xl p-8 md:p-12 text-center"
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.10)",
            backdropFilter: "blur(8px)",
            boxShadow: "0 30px 80px -30px rgba(0,0,0,0.6)",
          }}
        >
          <div className="flex justify-center mb-5">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{ background: "rgba(213,93,57,0.20)", border: "1px solid rgba(213,93,57,0.40)" }}
            >
              {unlocked
                ? <PartyPopper size={28} style={{ color: "var(--np-secondary)" }} />
                : <Gift size={28} style={{ color: "var(--np-secondary)" }} />}
            </div>
          </div>

          {!unlocked ? (
            <>
              <p className="text-[11px] tracking-[0.28em] uppercase font-semibold mb-3" style={{ color: "var(--np-secondary)" }}>
                Regalo personale da Alessandro
              </p>
              {firstName && (
                <p
                  data-testid="unlock-greeting"
                  className="text-base md:text-lg mb-3"
                  style={{ color: "rgba(255,255,255,0.75)", fontFamily: "Merriweather, serif" }}
                >
                  Ciao <span style={{ color: "#fff", fontWeight: 600 }}>{firstName}</span>,
                </p>
              )}
              <h1
                className="text-3xl md:text-[44px] leading-[1.08] tracking-tight"
                style={{ fontFamily: "Merriweather, serif" }}
              >
                {cfg.headline}{" "}
                <span className="italic" style={{ color: "var(--np-secondary)" }}>
                  {cfg.highlight}.
                </span>
              </h1>
              <p className="mt-5 text-lg leading-[1.7]" style={{ color: "rgba(255,255,255,0.85)" }}>
                {cfg.tagline}
              </p>

              <ul className="mt-8 grid gap-3 text-left">
                {cfg.bullets.map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-3 p-3 rounded-xl"
                    style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
                  >
                    <Sparkles size={16} className="mt-0.5 flex-shrink-0" style={{ color: "var(--np-secondary)" }} />
                    <span className="text-[14.5px]" style={{ color: "rgba(255,255,255,0.88)" }}>{b}</span>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={handleUnlock}
                disabled={submitting}
                data-testid="unlock-confirm-btn"
                className="cta-btn inline-flex items-center gap-2 mt-9 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting
                  ? <><Loader2 size={16} className="animate-spin" /> Sblocco in corso…</>
                  : <>{cfg.ctaLabel}</>}
              </button>

              {error && (
                <p className="mt-5 text-sm" data-testid="unlock-error" style={{ color: "#fca5a5" }}>{error}</p>
              )}
            </>
          ) : (
            <>
              <p className="text-[11px] tracking-[0.28em] uppercase font-semibold mb-3" style={{ color: "#86efac" }}>
                🎉 Sbloccato!
              </p>
              <h1
                className="text-3xl md:text-[44px] leading-[1.08] tracking-tight"
                style={{ fontFamily: "Merriweather, serif" }}
              >
                Fatto. Ora{" "}
                <span className="italic" style={{ color: "var(--np-secondary)" }}>
                  {cfg.highlight}
                </span>{" "}
                {kind === "full_course" ? "è tuo." : "sono tue."}
              </h1>
              <p className="mt-5 text-lg leading-[1.7]" style={{ color: "rgba(255,255,255,0.85)" }}>
                Accesso immediato — buona visione 💫
              </p>

              <a
                href={cfg.successHref}
                data-testid="unlock-go-btn"
                className="cta-btn inline-flex items-center gap-2 mt-9"
              >
                {cfg.successCta}
              </a>

              <div className="mt-7 flex items-center justify-center gap-2 text-[13px]" style={{ color: "rgba(255,255,255,0.7)" }}>
                <CheckCircle2 size={14} style={{ color: "#86efac" }} />
                <span>Conservato nel tuo account: <strong>{me?.email}</strong></span>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
